import { StatusPill, type Tone } from "@/components/ui";
import type { DeploymentStatus, TenantStatus } from "@/types/api";

// Each status is a row of a table `state → tone`: what the state MEANS to the
// operator, not which colour it wears (the tone owns that, in one place).
interface StatusRow {
  tone: Tone;
  label: string;
}

const TENANT_STATUS: Record<TenantStatus, StatusRow> = {
  draft: { tone: "neutral", label: "Draft" },
  active: { tone: "success", label: "Active" },
  suspended: { tone: "warning", label: "Suspended" },
  archived: { tone: "neutral", label: "Archived" },
};

const DEPLOYMENT_STATUS: Record<DeploymentStatus, StatusRow> = {
  triggered: { tone: "neutral", label: "Queued" },
  in_progress: { tone: "info", label: "In progress" },
  success: { tone: "success", label: "Succeeded" },
  partial_failure: { tone: "warning", label: "Partial" },
  failed: { tone: "danger", label: "Failed" },
  unknown: { tone: "neutral", label: "Unknown" },
};

/** The words of a deployment status, for a place where only a dot draws it. */
export function deploymentStatusLabel(status: DeploymentStatus): string {
  return DEPLOYMENT_STATUS[status]?.label ?? status;
}

export function TenantStatusBadge({ status }: { status: TenantStatus }) {
  // A status the table does not know yet (the API is ahead of the GUI) shows as
  // itself, neutral, rather than as nothing.
  const row = TENANT_STATUS[status] ?? { tone: "neutral", label: status };
  return <StatusPill tone={row.tone}>{row.label}</StatusPill>;
}

export function DeploymentStatusBadge({ status }: { status: DeploymentStatus }) {
  const row = DEPLOYMENT_STATUS[status] ?? { tone: "neutral", label: status };
  return <StatusPill tone={row.tone}>{row.label}</StatusPill>;
}

/**
 * Generic readiness pill — "Prêt" / "En préparation" — used for derived
 * business states (connexion, environnement) so the GUI never surfaces the
 * raw identifier that backs the state.
 */
export function ReadinessBadge({
  ready,
  readyLabel = "Ready",
  pendingLabel = "Pending",
  pulse = false,
}: {
  ready: boolean;
  readyLabel?: string;
  pendingLabel?: string;
  /** The pending state is being waited on right now (the dot pulses). */
  pulse?: boolean;
}) {
  return ready ? (
    <StatusPill tone="success">{readyLabel}</StatusPill>
  ) : (
    <StatusPill tone="warning" pulse={pulse}>
      {pendingLabel}
    </StatusPill>
  );
}
